import type { LucideIcon } from "lucide-react";
import { Check } from "lucide-react";
import { ProBadge } from "@/components/ui/ProBadge";

export interface VisualChoiceOption<T extends string | number> {
  value: T;
  title: string;
  description?: string;
  icon?: LucideIcon;
  preview?: React.ReactNode;
  isPro?: boolean;
  disabled?: boolean;
}

export interface VisualChoiceCardProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: VisualChoiceOption<T>[];
  columns?: 1 | 2 | 3 | 4;
  disabled?: boolean;
  onUpgradeClick?: (featureKey?: string) => void;
  className?: string;
  ariaLabel?: string;
}

export function VisualChoiceCard<T extends string | number>({
  value,
  onChange,
  options,
  columns = 2,
  disabled = false,
  onUpgradeClick,
  className = "",
  ariaLabel,
}: VisualChoiceCardProps<T>) {
  const colClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-4",
  }[columns];

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`grid ${colClass} gap-2.5 ${className}`}
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        const Icon = option.icon;
        const isItemDisabled = disabled || option.disabled;

        const handleClick = () => {
          if (isItemDisabled) return;
          if (option.isPro && onUpgradeClick) {
            onUpgradeClick();
            return;
          }
          onChange(option.value);
        };

        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={isItemDisabled}
            onClick={handleClick}
            className={`group relative flex flex-col justify-between p-3.5 rounded-xl border text-left transition-all outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary ${
              isSelected
                ? "border-primary bg-primary/5 ring-1 ring-primary shadow-xs"
                : "border-border bg-card/60 hover:bg-card hover:border-primary/40 shadow-2xs"
            } ${isItemDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            {/* Visual Preview or Icon */}
            {option.preview ? (
              <div className="mb-2.5 w-full rounded-lg overflow-hidden border border-border/70 bg-muted/30">
                {option.preview}
              </div>
            ) : Icon ? (
              <div className="flex items-center justify-between w-full mb-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                    isSelected
                      ? "bg-primary text-primary-foreground shadow-2xs"
                      : "bg-muted text-muted-foreground group-hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                {isSelected && (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xs">
                    <Check className="h-3 w-3" />
                  </div>
                )}
              </div>
            ) : null}

            {/* Title & Metadata */}
            <div className="w-full">
              <div className="flex items-center justify-between gap-1.5 mb-0.5">
                <span className="text-xs sm:text-sm font-semibold text-foreground truncate">
                  {option.title}
                </span>
                {option.isPro && <ProBadge locked />}
                {!option.preview && !Icon && isSelected && (
                  <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                    <Check className="h-2.5 w-2.5" />
                  </div>
                )}
              </div>
              {option.description && (
                <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                  {option.description}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
